import { useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import {
  getBrandImportSources,
  getReferencesForBrand,
  getImportConfigForBrand,
} from '../../services/firebase';
import {
  fetchShopifyProducts,
  getProductTypes,
  filterProducts,
  titleExcludedProducts,
  parseProducts,
  isLikelyWatch,
  type ShopifyProduct,
} from './ShopifyScraper';
import { applyExclusionRules, type ExclusionRule } from './ImportFieldRules';
import type { ShopifyImportSource, WatchBrand } from '../../types';
import toast from 'react-hot-toast';

interface ProbeShopifyProps {
  brand: WatchBrand;
}

/** One row of the per-product breakdown — a product that survives every gate. */
interface ProductRow {
  handle: string;
  title: string;
  type: string;
  /** Variants left after custom exclusion rules + the imageless gate. */
  survivingVariants: number;
}

interface ProbeResult {
  url: string;
  products: ShopifyProduct[];
  types: { type: string; count: number }[];
  /** Saved exclusion config from brand_import_sources (read-only, for reference). */
  excludedProductTypes: string[];
  /**
   * ID-model breakdown of the brand's existing refs (Phase 1.5, commit 884cb0c).
   * Composite-ID docs (`shopify__…`) re-import in place; legacy-ID docs would be
   * duplicated by parallel composite docs on re-import.
   */
  compositeIdRefs: number;
  legacyIdRefs: number;
  /**
   * All existing ref doc ids for this brand. Used to decide, per multi-variant
   * product in the selection, whether its parent doc (`shopify__{id}__parent`)
   * already exists (→ re-import updates it) or not (→ re-import creates it).
   */
  existingRefIds: Set<string>;
  /**
   * The brand's saved custom exclusion rules (admin_config/import_rules_{brandId},
   * via getImportConfigForBrand) — per-brand title/field keyword excludes James
   * configures in the Bulk Import wizard. The importer applies these (enabled
   * ones) to drop matching parsed entries; the probe mirrors that so its counts
   * reflect the same survivors. Empty when the brand has none.
   */
  exclusionRules: ExclusionRule[];
}

/**
 * Read-only signal for whether a Shopify-sourced brand is worth a delete-all +
 * re-import. Mirrors the importer's COMPLETE fetch→parents pipeline (see the
 * analysis memo): product-type checkboxes + built-in EXCLUDE_TITLE, then
 * parseProducts, the brand's saved custom exclusion rules, and the imageless
 * gate. A product is counted as a re-import parent only when ≥2 of its variants
 * survive all of those — so the count matches what a real import would write.
 *
 * The type-checkbox list is pre-set to the brand's saved exclusion config and
 * toggling is a sandbox: it changes the live preview, never the saved config.
 * No Firestore writes.
 */
export function ProbeShopify({ brand }: ProbeShopifyProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ProbeResult | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set());
  const [showBreakdown, setShowBreakdown] = useState(false);

  const analyze = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    setSelectedTypes(new Set());
    setShowBreakdown(false);
    try {
      const sourcesDoc = await getBrandImportSources(brand.id);
      const shopifySource = sourcesDoc?.sources.find(
        (s): s is ShopifyImportSource => s.kind === 'shopify',
      );
      const url = (shopifySource?.storeUrl || brand.shopifyUrl || '')
        .trim()
        .replace(/\/+$/, '');
      if (!url) {
        setError('No Shopify store URL configured for this brand.');
        return;
      }

      const products = await fetchShopifyProducts(url);
      const types = getProductTypes(products);
      const excludedProductTypes = shopifySource?.excludedProductTypes || [];

      // ID-model breakdown of existing refs: composite IDs (`shopify__…`) are on
      // the Phase 1.5 model and re-import in place; everything else is a legacy
      // hash ID that a re-import would duplicate. Counting by doc-id prefix only —
      // no field reads needed. Reuse the existing brand-refs query helper.
      const existingRefs = await getReferencesForBrand(brand.id, 10000);
      const existingRefIds = new Set(existingRefs.map((r) => r.id));
      let compositeIdRefs = 0;
      let legacyIdRefs = 0;
      for (const ref of existingRefs) {
        if (ref.id.startsWith('shopify__')) compositeIdRefs += 1;
        else legacyIdRefs += 1;
      }

      // The brand's custom exclusion rules (same source the importer reads at
      // dialog-open). PersistedExclusionRule lacks the `id` ExclusionRule needs
      // for applyExclusionRules' match tally — synthesize one. Non-fatal: a brand
      // with no config just gets an empty rule set. applyExclusionRules itself
      // skips disabled rules, so no pre-filtering needed.
      let exclusionRules: ExclusionRule[] = [];
      try {
        const config = await getImportConfigForBrand(brand.id);
        exclusionRules = config.exclusionRules.map((r, i) => ({
          ...r,
          id: `probe-excl-${i}`,
          condition: r.condition as ExclusionRule['condition'],
        }));
      } catch {
        /* no saved config — proceed with no custom rules */
      }

      // Initial checkbox state mirrors the saved exclusion config: a type is
      // checked (included) when it's NOT in the exclude list. Types never seen
      // before (not in the list) default to checked.
      const excludeSet = new Set(excludedProductTypes);
      const selected = new Set(
        types.filter((t) => !excludeSet.has(t.type)).map((t) => t.type),
      );

      setResult({
        url,
        products,
        types,
        excludedProductTypes,
        compositeIdRefs,
        legacyIdRefs,
        existingRefIds,
        exclusionRules,
      });
      setSelectedTypes(selected);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to probe Shopify store';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // Mirror the importer's COMPLETE fetch→parents pipeline, recomputed live on
  // every type toggle. A Shopify product becomes a parent when ≥2 of its variants
  // survive ALL of the importer's gates; exactly 1 survivor → standalone; 0 → the
  // product drops out entirely. The gates, in importer order:
  //   1. filterProducts — selected product types + the built-in title rule
  //      (whole-word accessory words such as strap/bracelet/tool, unless the
  //      title also carries a watch signal). [BulkImport.handleShopifyProceed]
  //      The import dialog lets a curator tick an excluded product back in;
  //      the probe shows the rule's verdict without overrides.
  //   2. parseProducts — one entry per Shopify variant, carrying imgSrc,
  //      shopifyProductId, and the fields custom rules match on.
  //   3. applyExclusionRules — the brand's saved per-brand keyword/field excludes
  //      (getImportConfigForBrand). [field-rules step → applyExclusionRules]
  //   4. imageless gate — items are built `included: !!entry.imgSrc`, so a variant
  //      with no resolvable image is dropped. [BulkImport item build]
  // Within the multi-variant set, would-create vs would-update is decided by
  // whether the parent doc (`shopify__{id}__parent`) already exists in Firestore.
  const titleExcluded = useMemo(
    () => (result ? titleExcludedProducts(result.products, selectedTypes) : []),
    [result, selectedTypes],
  );

  const { multiVariant, singleVariant, wouldCreate, wouldUpdate, rows } = useMemo(() => {
    const empty = {
      multiVariant: 0,
      singleVariant: 0,
      wouldCreate: 0,
      wouldUpdate: 0,
      rows: [] as ProductRow[],
    };
    if (!result) return empty;

    const filtered = filterProducts(result.products, selectedTypes); // gate 1
    const entries = parseProducts(filtered, result.url); // gate 2
    const { excludedIndices } = applyExclusionRules(entries, result.exclusionRules); // gate 3

    // Surviving variant count per Shopify product id (gates 3 + 4 applied here).
    const survivorsByProduct = new Map<string, number>();
    entries.forEach((e, idx) => {
      const survives = !!e.imgSrc && !excludedIndices.has(idx); // gate 4 + gate 3
      if (!survives || !e.shopifyProductId) return;
      survivorsByProduct.set(
        e.shopifyProductId,
        (survivorsByProduct.get(e.shopifyProductId) ?? 0) + 1,
      );
    });

    // Display metadata pulled from the raw Shopify product (entries don't carry
    // product_type). Keyed by the same String(id) the parser stamps.
    const productById = new Map(result.products.map((p) => [String(p.id), p]));

    let multi = 0;
    let single = 0;
    let create = 0;
    let update = 0;
    const rows: ProductRow[] = [];
    for (const [productId, survivors] of survivorsByProduct) {
      if (survivors < 1) continue;
      const p = productById.get(productId);
      rows.push({
        handle: p?.handle || productId,
        title: p?.title || '—',
        type: p?.product_type || '(none)',
        survivingVariants: survivors,
      });
      if (survivors >= 2) {
        multi += 1;
        const parentId = `shopify__${productId}__parent`;
        if (result.existingRefIds.has(parentId)) update += 1;
        else create += 1;
      } else {
        single += 1;
      }
    }
    rows.sort((a, b) => b.survivingVariants - a.survivingVariants);
    return {
      multiVariant: multi,
      singleVariant: single,
      wouldCreate: create,
      wouldUpdate: update,
      rows,
    };
  }, [result, selectedTypes]);

  const handleOpen = () => {
    setIsOpen(true);
    void analyze();
  };

  const handleClose = () => {
    if (loading) return;
    setIsOpen(false);
    setResult(null);
    setSelectedTypes(new Set());
    setError('');
    setShowBreakdown(false);
  };

  return (
    <>
      <Button variant="secondary" onClick={handleOpen}>
        Check Shopify variants
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={`Shopify variant check — ${brand.displayName || brand.name}`}
        size="lg"
      >
        <div className="space-y-4">
          {loading && (
            <p className="text-sm text-gray-600">
              Fetching products.json… (read-only — nothing is written)
            </p>
          )}

          {!loading && error && (
            <div className="space-y-4">
              <p className="text-sm text-red-600">{error}</p>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => void analyze()}>
                  Retry
                </Button>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </div>
          )}

          {!loading && result && (
            <>
              <p className="text-xs text-gray-400 break-all">{result.url}</p>

              <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                Selection mirrors your saved exclusion config. Toggle to preview
                different scenarios. Changes here aren't saved — use Bulk Import to
                update the actual config.
              </p>

              {titleExcluded.length > 0 && (
                <details className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                  <summary className="cursor-pointer font-medium text-amber-900">
                    {titleExcluded.length} product{titleExcluded.length !== 1 ? 's' : ''} excluded by title
                  </summary>
                  <p className="mt-1 text-amber-800">
                    An accessory word in the title and nothing marking it as a watch. In Bulk Import these can
                    be ticked back in.
                  </p>
                  <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-gray-800">
                    {titleExcluded.map(({ product, keyword }) => (
                      <li key={product.id}>
                        {product.title} <span className="text-gray-500">— matched “{keyword}”</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="grid grid-cols-2 gap-2 text-center">
                <Stat
                  label="Multi-variant (selected)"
                  value={multiVariant}
                  accent={multiVariant > 0 ? 'emerald' : undefined}
                />
                <Stat label="Single-variant (selected)" value={singleVariant} />
              </div>

              {multiVariant > 0 && (
                <div className="grid grid-cols-2 gap-2 text-center">
                  <Stat
                    label="Would create new parents"
                    value={wouldCreate}
                    accent={wouldCreate > 0 ? 'emerald' : undefined}
                  />
                  <Stat label="Would update existing parents" value={wouldUpdate} />
                </div>
              )}

              {/* ID-model breakdown of existing refs (Phase 1.5). */}
              <div className="grid grid-cols-2 gap-2 text-center">
                <Stat
                  label="Composite-ID refs"
                  value={result.compositeIdRefs}
                  accent={result.compositeIdRefs > 0 ? 'emerald' : undefined}
                />
                <Stat
                  label="Legacy-ID refs"
                  value={result.legacyIdRefs}
                  accent={result.legacyIdRefs > 0 ? 'amber' : undefined}
                />
              </div>

              {result.legacyIdRefs > 0 ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  <span className="font-semibold">
                    {result.legacyIdRefs} ref
                    {result.legacyIdRefs === 1 ? '' : 's'} use legacy IDs.
                  </span>{' '}
                  Re-importing will create duplicates — delete refs first.
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                  All refs use composite IDs — re-import will update in place, no
                  duplicates.
                </div>
              )}

              {/* Combined verdict across three dimensions: selected types (drives
                  the multi-variant set), whether that set creates NEW parent docs
                  vs updates existing ones, and whether any legacy-ID refs in
                  Firestore would be duplicated by a re-import. */}
              <div
                className={
                  result.legacyIdRefs > 0
                    ? 'rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'
                    : wouldCreate > 0
                      ? 'rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800'
                      : 'rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700'
                }
              >
                <span className="font-semibold">Verdict: </span>
                {multiVariant === 0 && result.legacyIdRefs === 0 && (
                  <>
                    Re-import is safe. In-place updates only, no new parent/variant
                    structure.
                  </>
                )}
                {multiVariant === 0 && result.legacyIdRefs > 0 && (
                  <>
                    No new parent structure to create, BUT {result.legacyIdRefs}{' '}
                    legacy ref{result.legacyIdRefs === 1 ? '' : 's'} would be
                    duplicated. Delete + re-import recommended.
                  </>
                )}
                {multiVariant > 0 && wouldCreate === 0 && (
                  <>
                    Re-import will update {wouldUpdate} parent
                    {wouldUpdate === 1 ? '' : 's'} in place. No new docs.
                  </>
                )}
                {multiVariant > 0 && wouldCreate > 0 && (
                  <>
                    Re-import will create {wouldCreate} new parent
                    {wouldCreate === 1 ? '' : 's'}
                    {wouldUpdate > 0 && (
                      <>
                        {' '}
                        and update {wouldUpdate} existing one
                        {wouldUpdate === 1 ? '' : 's'}
                      </>
                    )}
                    .
                  </>
                )}
                {multiVariant > 0 && result.legacyIdRefs > 0 && (
                  <>
                    {' '}
                    <span className="font-semibold">
                      {result.legacyIdRefs} legacy ref
                      {result.legacyIdRefs === 1 ? '' : 's'} would also be
                      duplicated — delete + re-import required.
                    </span>
                  </>
                )}
              </div>

              {/* Type selection — mirrors the Bulk Import wizard's renderShopifyTypes */}
              <div className="border rounded-lg divide-y">
                {result.types.map(({ type, count }) => (
                  <label
                    key={type}
                    className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer ${
                      selectedTypes.has(type) ? '' : 'opacity-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypes.has(type)}
                      onChange={() => toggleType(type)}
                      className="rounded border-gray-300"
                    />
                    <span className="flex-1 text-sm font-medium text-gray-800">
                      {type === '(none)' ? '(none) — no product type' : type}
                    </span>
                    <span className="text-xs text-gray-400">
                      {count} product{count !== 1 ? 's' : ''}
                    </span>
                    {isLikelyWatch(type) && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        Watch
                      </span>
                    )}
                  </label>
                ))}
              </div>

              <p className="text-xs text-gray-400">
                {selectedTypes.size} type{selectedTypes.size !== 1 ? 's' : ''} selected
                • {rows.length} product{rows.length === 1 ? '' : 's'} • Title
                exclusions (straps, bracelets, tools…)
                {result.exclusionRules.length > 0
                  ? `, ${result.exclusionRules.length} custom rule${
                      result.exclusionRules.length === 1 ? '' : 's'
                    },`
                  : ''}{' '}
                and the imageless filter applied
              </p>

              {rows.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowBreakdown((v) => !v)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline"
                  >
                    {showBreakdown ? 'Hide' : 'Show'} per-product breakdown (
                    {rows.length})
                  </button>
                  {showBreakdown && (
                    <div className="mt-2 max-h-72 overflow-y-auto border rounded-lg divide-y">
                      {rows.map((p) => (
                        <div
                          key={p.handle}
                          className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                        >
                          <div className="min-w-0">
                            <div className="text-gray-800 truncate" title={p.title}>
                              {p.title}
                            </div>
                            <div className="text-gray-400 text-xs truncate font-mono">
                              {p.handle} · {p.type}
                            </div>
                          </div>
                          <span
                            className={
                              p.survivingVariants >= 2
                                ? 'shrink-0 text-emerald-700 text-xs font-medium'
                                : 'shrink-0 text-gray-400 text-xs'
                            }
                          >
                            {p.survivingVariants} variant
                            {p.survivingVariants === 1 ? '' : 's'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => void analyze()}>
                  Re-run
                </Button>
                <Button onClick={handleClose}>Close</Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'emerald' | 'amber';
}) {
  return (
    <div className="rounded-lg border border-gray-200 px-2 py-2">
      <div
        className={
          accent === 'emerald'
            ? 'text-lg font-semibold text-emerald-600'
            : accent === 'amber'
              ? 'text-lg font-semibold text-amber-600'
              : 'text-lg font-semibold text-gray-900'
        }
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}
